import { memo, useEffect, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'

import { interpolatePosition, type LatLng } from '../../utils/distance'

const MOVE_ANIMATION_MS = 900

interface MarkerHandle {
  setPosition: (position: LatLng) => void
  setZIndex: (zIndex: number) => void
  remove: () => void
}

/** Anchors content bottom-centre at the position, matching AdvancedMarkerElement's default. */
function createOverlayHandle(map: google.maps.Map, container: HTMLElement, initial: LatLng, zIndex: number): MarkerHandle {
  class HtmlOverlay extends google.maps.OverlayView {
    position: LatLng

    constructor(position: LatLng) {
      super()
      this.position = position
    }

    onAdd() {
      google.maps.OverlayView.preventMapHitsAndGesturesFrom(container)
      this.getPanes()?.overlayMouseTarget.appendChild(container)
    }

    draw() {
      const point = this.getProjection()?.fromLatLngToDivPixel(this.position)
      if (!point) return
      container.style.transform = `translate(${point.x}px, ${point.y}px) translate(-50%, -100%)`
    }

    onRemove() {
      container.remove()
    }
  }

  container.style.position = 'absolute'
  container.style.left = '0'
  container.style.top = '0'
  container.style.width = 'max-content'
  container.style.zIndex = String(zIndex)

  const overlay = new HtmlOverlay(initial)
  overlay.setMap(map)

  return {
    setPosition: (position) => {
      overlay.position = position
      overlay.draw()
    },
    setZIndex: (value) => {
      container.style.zIndex = String(value)
    },
    remove: () => overlay.setMap(null),
  }
}

function createAdvancedHandle(map: google.maps.Map, container: HTMLElement, initial: LatLng, zIndex: number): MarkerHandle {
  const marker = new google.maps.marker.AdvancedMarkerElement({
    map,
    position: initial,
    content: container,
    zIndex,
  })
  return {
    setPosition: (position) => {
      marker.position = position
    },
    setZIndex: (value) => {
      marker.zIndex = value
    },
    remove: () => {
      marker.map = null
    },
  }
}

/**
 * Renders React content as a map marker. Uses AdvancedMarkerElement when a mapId is configured,
 * otherwise an OverlayView. Position changes are interpolated imperatively (no React re-render
 * per frame) so markers glide instead of jumping.
 */
export const MapHtmlMarker = memo(function MapHtmlMarker({
  map,
  lat,
  lng,
  zIndex = 1,
  animate = false,
  useAdvanced,
  children,
}: {
  map: google.maps.Map
  lat: number
  lng: number
  zIndex?: number
  animate?: boolean
  useAdvanced: boolean
  children: ReactNode
}) {
  const [container] = useState(() => document.createElement('div'))
  const handleRef = useRef<MarkerHandle | null>(null)
  const currentRef = useRef<LatLng>({ lat, lng })
  const zIndexRef = useRef(zIndex)

  useEffect(() => {
    const canUseAdvanced = useAdvanced && Boolean(google.maps.marker?.AdvancedMarkerElement)
    const handle = canUseAdvanced
      ? createAdvancedHandle(map, container, currentRef.current, zIndexRef.current)
      : createOverlayHandle(map, container, currentRef.current, zIndexRef.current)
    handleRef.current = handle
    return () => {
      handle.remove()
      handleRef.current = null
    }
  }, [container, map, useAdvanced])

  useEffect(() => {
    zIndexRef.current = zIndex
    handleRef.current?.setZIndex(zIndex)
  }, [zIndex])

  useEffect(() => {
    const from = currentRef.current
    const to = { lat, lng }
    if (from.lat === to.lat && from.lng === to.lng) return undefined

    if (!animate || typeof requestAnimationFrame !== 'function') {
      currentRef.current = to
      handleRef.current?.setPosition(to)
      return undefined
    }

    let frame = 0
    const startedAt = performance.now()
    const step = (time: number) => {
      const progress = Math.min(1, (time - startedAt) / MOVE_ANIMATION_MS)
      const eased = 1 - (1 - progress) ** 3
      const position = interpolatePosition(from, to, eased)
      currentRef.current = position
      handleRef.current?.setPosition(position)
      if (progress < 1) frame = requestAnimationFrame(step)
    }
    frame = requestAnimationFrame(step)
    return () => cancelAnimationFrame(frame)
  }, [animate, lat, lng])

  return createPortal(children, container)
})
